import React, { useState } from 'react'

import { Input } from '@components'
import Button from '@components/Button'
import Spinner from '@components/Spinner'
import Status from '@components/Status'
import {
	CURATED_PROVISION_STATUSES,
	describeProvisionStatus,
	LegalProvision,
	LegalRedaction,
} from '@models/legalDocument.model'
import { legalDocumentApi } from '@services/LegalDocumentApi'

/*
 * Os dispositivos da norma, que é o que a tela de detalhe não tinha.
 *
 * Ela mostrava `provisionCount` e mais nada: dezenas de milhares de redações no
 * acervo e nenhum caminho para abrir uma. É aqui que o anexo que entrou como
 * inciso aparece, e é aqui que a norma vetorizada pela metade se denuncia.
 *
 * O texto não se edita por esta tela, e é decisão e não falta: todo texto do
 * acervo tem de existir na captura que o originou — é a regra que torna a
 * fundamentação conferível. Corrigir texto é recarregar o documento.
 */

const PER_PAGE = 50

const FILTERS: Array<{ label: string; value: string }> = [
	{ label: 'Todos', value: '' },
	{ label: 'Vigentes', value: 'vigente' },
	{ label: 'Revogados', value: 'revogado' },
	{ label: 'Inconstitucionais', value: 'inconstitucional' },
	{ label: 'Suspensos', value: 'suspenso' },
]

const styles = {
	filter: 'px-3 py-1 rounded-md text-sm font-medium',
	filterOn: 'bg-primary-transparent text-primary',
	filterOff: 'text-gray hover:text-primary',
	row: 'border border-gray-light rounded-md px-4 py-3 cursor-pointer hover:border-primary',
	rowOpen: 'border-primary',
	crumb: 'text-xs text-gray',
	address: 'font-mono text-sm text-primary',
}

/*
 * O que cada redação é, na ordem em que a página as conta.
 *
 * "Riscada" e "superada" não são a mesma coisa: o Planalto risca o que a emenda
 * trocou, e o texto plano de um PDF não tem risco nenhum — ali a redação antiga
 * só se distingue por vir antes da vigente.
 */
function describeRedaction(redaction: LegalRedaction): string {
	if (redaction.current) {
		return 'Vigente'
	}

	return redaction.struck ? 'Riscada na página' : 'Superada'
}

function Redactions({ id, path }: { id: string; path: string }) {
	const { data, isFetching } = legalDocumentApi.useProvisionQuery({ id, path })

	if (isFetching || !data) {
		return (
			<div className='mt-3'>
				<Spinner size='sm' />
			</div>
		)
	}

	return (
		<div className='mt-4 flex flex-col gap-3'>
			{data.notes.length > 0 && (
				<p className={styles.crumb}>Anotações: {data.notes.join(' ')}</p>
			)}

			{data.redactions.map((redaction) => (
				<div
					key={redaction.position}
					className={`px-4 py-3 rounded-md ${
						redaction.current ? 'bg-primary-transparent' : 'bg-gray-lighter'
					}`}
				>
					<div className='flex gap-3 items-center flex-wrap'>
						<span className='text-xs font-medium'>
							{describeRedaction(redaction)}
						</span>

						<span className={styles.crumb}>origem: {redaction.origin}</span>

						{redaction.amendedBy && (
							<span className={styles.crumb}>
								alterada por {redaction.amendedBy}
							</span>
						)}
					</div>

					<p className='mt-2 whitespace-pre-wrap leading-relaxed'>
						{redaction.text}
					</p>

					{redaction.annotation && (
						<p className={`${styles.crumb} mt-2`}>{redaction.annotation}</p>
					)}
				</div>
			))}

			{!data.embedded && (
				<p className='text-sm text-red'>
					Sem vetor: a busca por sentido não alcança este dispositivo, e ele só
					é achado por citação exata. Rode a vetorização do acervo.
				</p>
			)}
		</div>
	)
}

function Marker({
	id,
	path,
	status,
}: {
	id: string
	path: string
	status: string
}) {
	const [mark, { isLoading }] = legalDocumentApi.useMarkProvisionMutation()

	/*
	 * `revogado` não está aqui de propósito: a revogação é decisão do legislador
	 * e vem riscada na página da própria norma, na carga. Marcá-la à mão
	 * esconderia um defeito de segmentação em vez de corrigi-lo.
	 */
	return (
		<div className='mt-4 flex gap-2 items-center flex-wrap'>
			<span className={styles.crumb}>A curadoria marca:</span>

			{CURATED_PROVISION_STATUSES.map((candidate) => (
				<Button
					key={candidate}
					variant={candidate === status ? 'default' : 'outline'}
					disabled={isLoading || candidate === status}
					onClick={async (event) => {
						/* A linha inteira alterna o painel: marcar não pode fechá-lo. */
						event.stopPropagation()
						await mark({ id, path, status: candidate })
					}}
				>
					<>{describeProvisionStatus(candidate).label}</>
				</Button>
			))}
		</div>
	)
}

export default function Provisions({ id }: { id: string }) {
	const [page, setPage] = useState(1)
	const [search, setSearch] = useState('')
	const [status, setStatus] = useState('')
	const [open, setOpen] = useState<string | null>(null)

	const { data, isFetching } = legalDocumentApi.useProvisionsQuery({
		id,
		page,
		limit: PER_PAGE,
		...(search ? { search } : {}),
		...(status ? { status } : {}),
	})

	const [draft, setDraft] = useState('')

	const onFilter = (value: string) => {
		setStatus(value)
		setPage(1)
	}

	return (
		<div className='mt-6'>
			<div className='flex gap-4 items-center flex-wrap'>
				<form
					className='w-[420px]'
					onSubmit={(event) => {
						event.preventDefault()
						setSearch(draft)
						setPage(1)
					}}
				>
					<Input
						type='text'
						placeholder='Endereço ("art-5") ou palavra do texto'
						value={draft}
						onChange={(event) => setDraft(event.target.value)}
					/>
				</form>

				<div className='flex gap-1 flex-wrap'>
					{FILTERS.map((filter) => (
						<button
							key={filter.value}
							type='button'
							className={`${styles.filter} ${
								filter.value === status ? styles.filterOn : styles.filterOff
							}`}
							onClick={() => onFilter(filter.value)}
						>
							{filter.label}
						</button>
					))}
				</div>
			</div>

			<p className='text-sm text-gray mt-3'>
				A busca por palavra usa o mesmo índice que a conversão usa para achar o
				dispositivo — o que aparece aqui é o que ela acharia.
			</p>

			{isFetching && (
				<div className='flex justify-center mt-8'>
					<Spinner size='md' />
				</div>
			)}

			{!isFetching && data && (
				<>
					<p className='text-sm text-gray mt-4'>
						{data.meta.total} dispositivo
						{data.meta.total === 1 ? '' : 's'}
						{search || status ? ' no filtro' : ' na norma'}
					</p>

					<div className='flex flex-col gap-2 mt-3'>
						{data.data.map((provision: LegalProvision) => {
							const isOpen = open === provision.path
							const tone = describeProvisionStatus(provision.status)

							return (
								<div
									key={provision.path}
									className={`${styles.row} ${isOpen ? styles.rowOpen : ''}`}
									onClick={() => setOpen(isOpen ? null : provision.path)}
								>
									<div className='flex gap-3 items-center flex-wrap'>
										<span className={styles.address}>{provision.path}</span>
										<span className='font-medium'>{provision.label}</span>

										<Status status={tone.tone}>
											<>{tone.label}</>
										</Status>

										{provision.redactionCount > 1 && (
											<span className={styles.crumb}>
												{provision.redactionCount} redações
											</span>
										)}

										{!provision.embedded && (
											<span className='text-xs text-red'>sem vetor</span>
										)}
									</div>

									{provision.hierarchy.length > 0 && (
										<p className={`${styles.crumb} mt-1`}>
											{provision.hierarchy.join(' › ')}
										</p>
									)}

									{!isOpen && (
										<p className='mt-2 leading-relaxed'>
											{provision.text ??
												'Sem redação vigente: todas foram riscadas.'}
										</p>
									)}

									{isOpen && (
										<>
											<Redactions id={id} path={provision.path} />
											<Marker
												id={id}
												path={provision.path}
												status={provision.status}
											/>
										</>
									)}
								</div>
							)
						})}
					</div>

					{data.data.length === 0 && (
						<p className='text-gray mt-4'>
							Nenhum dispositivo com esse filtro.
						</p>
					)}

					{data.meta.lastPage > 1 && (
						<div className='mt-6 flex gap-3 items-center'>
							<Button
								variant='outline'
								disabled={page <= 1}
								onClick={() => setPage((current) => Math.max(1, current - 1))}
							>
								<>Anteriores</>
							</Button>

							<span className='text-sm text-gray'>
								página {data.meta.currentPage} de {data.meta.lastPage}
							</span>

							<Button
								variant='outline'
								disabled={page >= data.meta.lastPage}
								onClick={() =>
									setPage((current) =>
										Math.min(data.meta.lastPage, current + 1)
									)
								}
							>
								<>Seguintes</>
							</Button>
						</div>
					)}
				</>
			)}
		</div>
	)
}
